/**
 * 사례 이름 바꾸기(변조) — 매핑대로 **글자 그대로** 바꾼다(기계). AI 는 매핑만 제안한다(백엔드 tuningCase/redactSuggest).
 *
 * 경계는 백엔드 검증(validate.verifyRedaction · redactSuggest.wordRe)과 같다 — 영문·숫자·_ $ # 가 이어지면 같은 이름이다.
 * 그래서 RK01N006 을 바꿔도 PK_RK01N006 은 그대로다(따로 매핑해야 한다 — `relatedLeft` 가 짚어 준다).
 * 한 번에(정규식 하나로) 바꾼다 — 차례로 바꾸면 앞에서 바꾼 결과가 다음 매핑에 다시 걸릴 수 있다.
 * 대소문자는 가리지 않는다(Oracle 이름은 따옴표 없으면 대소문자 무관).
 */
const WORD = 'A-Za-z0-9_$#';
const IDENT_RE = /[A-Za-z][A-Za-z0-9_$#]{2,}/g;

const esc = s => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** 쓸 수 있는 매핑만 — 빈 칸·같은 이름은 뺀다(대소문자 무시로 원래 이름이 겹치면 앞의 것) */
export function cleanRows(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const from = String((r && r.from) || '').trim();
    const to = String((r && r.to) || '').trim();
    if (!from || !to || from.toUpperCase() === to.toUpperCase() || seen.has(from.toUpperCase())) continue;
    seen.add(from.toUpperCase());
    out.push({ from, to });
  }
  return out;
}

function matcher(rows) {
  const list = cleanRows(rows).sort((a, b) => b.from.length - a.from.length);
  if (!list.length) return null;
  const to = new Map(list.map(r => [r.from.toUpperCase(), r.to]));
  const re = new RegExp(`(^|[^${WORD}])(${list.map(r => esc(r.from)).join('|')})(?=[^${WORD}]|$)`, 'gi');
  return { re, to };
}

/**
 * @param fields 양식(문자열 칸들)
 * @param rows   [{from,to}]
 * @returns { fields, counts: {원래이름: 바꾼 수}, total }
 */
export function applyMapping(fields, rows) {
  const m = matcher(rows);
  const counts = {};
  if (!m) return { fields, counts, total: 0 };
  const out = {};
  for (const [k, v] of Object.entries(fields || {})) {
    if (typeof v !== 'string' || !v) { out[k] = v; continue; }
    m.re.lastIndex = 0;
    out[k] = v.replace(m.re, (all, pre, name) => {
      const key = name.toUpperCase();
      counts[key] = (counts[key] || 0) + 1;
      return `${pre}${m.to.get(key)}`;
    });
  }
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return { fields: out, counts, total };
}

/** 규칙 항목 중 이 사례 글에 나오는 것만 — 규칙은 폴더 전체에 누적되므로 통째로 올리면 표가 남의 이름으로 찬다 */
export function rowsInCase(fields, rows) {
  const text = Object.values(fields || {}).filter(v => typeof v === 'string').join('\n');
  return cleanRows(rows).filter(r => new RegExp(`(^|[^${WORD}])${esc(r.from)}(?=[^${WORD}]|$)`, 'i').test(text));
}

/** 매핑의 원래 이름이 아직 남아 있는 것 — 적용 뒤에 0 이어야 한다 */
export function leftovers(fields, rows) {
  const text = Object.values(fields || {}).filter(v => typeof v === 'string').join('\n');
  return cleanRows(rows).filter(r => new RegExp(`(^|[^${WORD}])${esc(r.from)}(?=[^${WORD}]|$)`, 'i').test(text)).map(r => r.from);
}

/**
 * 함께 바꿀 만한 이름 — 매핑한 원래 이름을 **포함하는** 다른 영문 이름이 사례에 남아 있으면(예: RK01N006 만 바꾸고 PK_RK01N006 은 그대로).
 * 경계 규칙 때문에 따로 바뀌지 않으므로 알려 준다(막지는 않는다).
 */
export function relatedLeft(fields, rows) {
  const keys = cleanRows(rows).map(r => r.from.toUpperCase());
  if (!keys.length) return [];
  const text = Object.values(fields || {}).filter(v => typeof v === 'string').join('\n');
  const out = new Set();
  for (const m of text.matchAll(IDENT_RE)) {
    const id = m[0].toUpperCase();
    if (keys.includes(id)) continue;
    if (keys.some(k => k.length >= 3 && id.includes(k))) out.add(m[0]);
  }
  return [...out].slice(0, 20);
}
